import { inject, Injectable } from "@angular/core";
import { SyncStatusEnum } from "../../../../models/types/general-types";
import { BasiqValidator } from "./basiq.validator";
import { error } from "@angular/compiler-cli/src/transformers/util";
import { BasiqUserAction } from "@bitwarden/web-vault/app/services/syncing/basiq/actions/basiq.action.user";

@Injectable({
  providedIn: "root",
})
export class BasiqUserValidator extends BasiqValidator {
  syncStatus: SyncStatusEnum;
  private basiqUserAction: BasiqUserAction;

  constructor() {
    super();
    this.basiqUserAction = inject(BasiqUserAction);
  }

  /** If user is not in basiq or does not have consent, then he is not valid */
  async isValid(): Promise<boolean> {
    this.appState.startProcess("basiq-syncing-data", "Checking your credentials");
    if (!(await this.isValidUser())) {
      this.appState.endProcess("basiq-syncing-data");
      return false;
    }

    const isConsent = await this.isValidConsent();
    this.appState.endProcess("basiq-syncing-data");
    return isConsent;
  }

  /** Check if user exists in basiq */
  async isBasiqUser(): Promise<boolean> {
    try {
      const result = await this.basiqAuthApi.getUser();
      return result && !!result.user && !!result.user?.id;
    } catch (error) {
      /** we throw the error back to the consumer so the consumer knows we are not sure if user exists or not */
      await this.glossSyncService.logError(
        [],
        error,
        "basiq user check",
        SyncStatusEnum.unknownError,
      );
      this.glossSyncService.confirmationDialog.notify(
        "error",
        "error",
        "havingTroubleConnectingToBasiq",
      );
      throw error;
    }
  }

  async isUserConsent(): Promise<boolean> {
    try {
      const activeConsent = await this.basiqUserAction.getActiveConsent();
      return !!activeConsent;
    } catch (error) {
      /** we throw the error back to the consumer so the consumer knows we are not sure if user has consent or not */
      await this.glossSyncService.logError(
        [],
        error,
        "basiq consent check",
        SyncStatusEnum.unknownError,
      );
      this.glossSyncService.confirmationDialog.notify(
        "error",
        "error",
        "havingTroubleCheckingYourConsent",
      );
      throw error;
    }
  }

  /** Does user exist in Basiq? he has an id in Basiq dash? */
  private async isValidUser(): Promise<boolean> {
    try {
      const isBasiqUser = await this.isBasiqUser();
      if (!isBasiqUser) {
        const error = new Error("You are not a Basiq user. Please check your credentials.");
        await this.glossSyncService.logError(
          [],
          error,
          "basiq user check",
          SyncStatusEnum.noBasiqUser,
        );
        this.syncStatus = SyncStatusEnum.noBasiqUser;
      }

      return isBasiqUser;
    } catch (error) {
      await this.glossSyncService.logError(
        [],
        error,
        "basiq user check",
        SyncStatusEnum.unknownError,
      );
      this.syncStatus = SyncStatusEnum.unknownError;
      return false;
    }
  }

  /** Does user have an active consent(aka, authorization) */
  private async isValidConsent(): Promise<boolean> {
    try {
      const isConsent = await this.isUserConsent();
      if (!isConsent) {
        const error = new Error("You dont have consent. Please make sure you have consented.");
        await this.glossSyncService.logError(
          [],
          error,
          "basiq consent check",
          SyncStatusEnum.noBasiqConsent,
        );
        this.syncStatus = SyncStatusEnum.noBasiqConsent;
      }

      return isConsent;
    } catch (error) {
      await this.glossSyncService.logError(
        [],
        error,
        "basiq user check",
        SyncStatusEnum.unknownError,
      );
      this.syncStatus = SyncStatusEnum.unknownError;
      return false;
    }
  }
}
